import { Event } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Button, Input, Progress, styles, Text, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';

import { L10N } from './FormStripe.l10n';
import * as style from './FormStripe.module.css';
import { getStyle } from './herlpes';
import { loadScript } from '../../../../../helpers/loadScript';
import { trackError } from '../../../../../helpers/trackError';
import { EVENT } from '../../../../helpers';

const AUTO = 'auto';
const CARD = 'card';
const DEFAULT_BILLING_DETAILS = { address: { country: 'never' }, name: 'never' };

let checkout;

export const FormStripe = ({ publicKey, response }) => {
  const { translate } = useLocale();

  const [busy, setBusy] = useState();
  const [country, setCountry] = useState();
  const [name, setName] = useState();
  const [nameError, setNameError] = useState();
  const [ready, setReady] = useState();
  const [stripeReady, setStripeReady] = useState();
  const [type, setType] = useState();

  const refAdaptivePricing = useRef();
  const refElements = useRef();

  useEffect(() => {
    const domain = 'payments';
    const id = 'stripeScript';
    (async () => {
      await loadScript({
        async: false,
        domain,
        id,
        src: `https://js.stripe.com/dahlia/stripe.js`,
        onLoad: handleLoad,
      });
    })();

    return () => {
      const el = document.getElementById(`${domain}-${id}`);
      el && el.parentNode.removeChild(el);
    };
  }, [response]);

  useEffect(() => {
    if (!response || !ready) return;

    setBusy();
    setStripeReady();

    const {
      configuration: {
        adaptivePricing,
        cardHolderName,
        clientCountry,
        clientSecret,
        stripeAccountId: stripeAccount,
      } = {},
    } = response;
    setCountry(clientCountry);
    if (cardHolderName) setName(cardHolderName);

    (async () => {
      checkout = window.Stripe(publicKey, { stripeAccount }).initCheckoutElementsSdk({
        adaptivePricing,
        clientSecret,
        elementsOptions: { appearance: { theme: 'stripe', variables: getStyle() } },
      });
      checkout.loadFonts([
        { cssSrc: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&display=swap' },
      ]);

      const paymentElement = checkout.createPaymentElement({
        layout: 'tabs',
        fields: { billingDetails: clientCountry ? DEFAULT_BILLING_DETAILS : AUTO },
      });

      paymentElement.on('ready', () => {
        setStripeReady(true);
      });

      paymentElement.on('change', (event) => {
        const { value = {} } = event;
        setType(value.type);

        checkout.getPaymentElement().update({
          fields: { billingDetails: value.type === CARD && clientCountry ? DEFAULT_BILLING_DETAILS : AUTO },
        });
      });

      if (adaptivePricing?.allowed) checkout.createCurrencySelectorElement().mount(refAdaptivePricing.current);

      paymentElement.mount(refElements.current);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, response]);

  const handleNameChange = (value) => {
    if (value) setNameError();
    setName(value);
  };

  const handleLoad = async () => {
    setReady(true);
  };

  const handleError = (error) => {
    trackError('Stripe', error instanceof Error ? error.stack : error?.message);
    Event.publish(EVENT.NOTIFICATION, {
      error: true,
      defaultMessage: translate(L10N.NOTIFICATION_ERROR),
    });
  };

  const handlePress = async () => {
    if (!checkout) return;

    setBusy(true);

    try {
      const loadActionsResult = await checkout.loadActions();
      if (loadActionsResult.type === 'success') {
        const session = await loadActionsResult.actions.getSession();
        if (!name && type === CARD && country) {
          setNameError(true);
          if (session.canConfirm) return;
        }

        const { error } = await loadActionsResult.actions.confirm(
          type === CARD && name ? { billingAddress: { name, address: { country } } } : undefined,
        );

        if (session.canConfirm && error) handleError(error);
      } else handleError(loadActionsResult.error);
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Progress indeterminate visible={!stripeReady} />
      <View className={style.container}>
        <View ref={refAdaptivePricing} />
        <View ref={refElements} />
        {type === CARD && country && (
          <View className={style.form}>
            <Text action label-for="name">
              {translate(L10N.LABEL_CARD_NAME)}
            </Text>
            <Input
              name="name"
              value={name}
              onChange={handleNameChange}
              className={styles(style.name, nameError && style.error)}
            />
            {nameError && (
              <Text action error>
                {translate(L10N.LABEL_CARD_NAME_ERROR)}
              </Text>
            )}
          </View>
        )}
      </View>
      {!!stripeReady && (
        <Button busy={busy} onPress={handlePress} className={style.button}>
          {translate(L10N.ACTION_PAY)}
        </Button>
      )}
    </>
  );
};

FormStripe.displayName = 'Mirai:Payments:ButtonPayment:FormStripe';

FormStripe.propTypes = {
  publicKey: PropTypes.string.isRequired,
  response: PropTypes.object,
};
